import React from 'react';
import { adjustBrandColor } from '../../utils/brand-color/adjust-brand-color';

export interface BrandPanelProps {
  /** Raw extracted brand hex (AlgoliaCompanyRecord.brandColour / DisplayJob.brandColour). */
  brandColour: string | null | undefined;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

/**
 * Shared brand-color-themed panel: takes a company's raw brand hex through the same
 * adjustBrandColor() pipeline as the job detail page's BrandRail, then renders a deepened
 * gradient fill with legible on-color text. One implementation shared by the best-companies
 * podium, the company profile hero, and (wherever else) a company's own color should show
 * through — instead of each page re-deriving panel/on colors independently.
 */
export function BrandPanel({ brandColour, className = '', style, children }: BrandPanelProps) {
  const { panel, on } = adjustBrandColor(brandColour);
  return (
    <div
      className={`relative overflow-hidden text-[color:var(--panel-on)] ${className}`}
      style={{
        // color-mix darkens the panel color for the gradient's far stop without a second
        // adjustBrandColor call or a separate darken() util.
        background: `linear-gradient(165deg, ${panel} 0%, color-mix(in srgb, ${panel} 78%, black) 100%)`,
        ['--panel-on' as string]: on,
        ...style,
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(130% 100% at 88% -15%, rgba(255,255,255,0.16), transparent 55%)' }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}
